// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally re-exports OpenCode runtime helpers.
import { bridgeAgentControllerConversationRef, buildAdapterCancelRequest, buildDispatchSpaceActionMcpToolIds, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, resolveBuiltinAdapterConversationBridgeSpec, resolveDispatchSpaceActionMcpServer, verifyLaunchContractFiles, } from "@openmeld/adapter-core";
import { resolveOpenCodeTimeoutMs as resolveOpenCodeTimeoutMsLocal } from "./config/opencode-runtime-config.js";
import { invokeOpenCodeWithSingleContextHeal as invokeOpenCodeWithSingleContextHealLocal } from "./invoke/opencode-invoke.js";
export { resolveInstalledOpenCodeLaunchCandidates, resolveInstalledOpenCodeLaunchContract, resolveOpenCodeTimeoutMs, verifyOpenCodeLaunchContract, } from "./config/opencode-runtime-config.js";
export { isLikelyOpenCodeContextLostError } from "./context/opencode-context-heal.js";
export { invokeOpenCodeWithSingleContextHeal, OpenCodeRuntimeError, } from "./invoke/opencode-invoke.js";
export { parseOpenCodeOutputDocument, parseOpenCodeOutputLine, } from "./output/opencode-output-parser.js";
export { invokeOpenCodeCliTransport, OpenCodeCliTransportError, } from "./transport/opencode-cli-transport.js";
export { forkOpenCodeServerSession, invokeOpenCodeServerTransport, OpenCodeServerTransportError, } from "./transport/opencode-server-transport.js";
export { parseOpenCodeGoUsageResponse, } from "./usage/opencode-go-usage-limits.js";
const OPENCODE_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("opencode");
const OPENCODE_PERMISSION_ENV = "OPENCODE_PERMISSION";
const OPENCODE_CONFIG_CONTENT_ENV = "OPENCODE_CONFIG_CONTENT";
export const createOpenCodeAdapter = (options) => {
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt: options.sendInterrupt,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "opencode",
        bridgeSpec: OPENCODE_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: options.invokeByMode,
        repairConversationPolicy: "require_canonical",
        resolveDefaultInvoke: () => resolveDefaultOpenCodeInvoke(options),
        runtimeOptions: options,
    });
    return {
        adapterId: "opencode",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: controlShell.interrupt,
    };
};
export const buildOpenCodeInterruptRequest = (input) => ({
    mode: "graceful",
    reason: input.reason,
    taskId: input.taskId,
});
export const buildOpenCodeCancelRequest = (input) => buildAdapterCancelRequest({
    reason: input.reason,
    taskId: input.taskId,
});
const resolveDefaultOpenCodeInvoke = (options) => {
    const openCodeTimeoutMs = options.openCodeTimeoutMs ?? resolveOpenCodeTimeoutMsLocal();
    return async (input) => {
        const launchContract = await resolveOpenCodeDispatchLaunchContract({
            inputLaunchContract: input.launchContract,
            openCodeBin: options.openCodeBin,
            optionLaunchContract: options.launchContract,
        });
        const openCodeWorkdir = input.runtimeContext.providerRuntimeWorkingDirectory;
        const onProcessControlEvent = resolveRuntimeProcessRegistrar({
            registerRuntimeProcess: options.registerRuntimeProcess,
            taskId: input.taskId,
        });
        const result = await invokeOpenCodeWithSingleContextHealLocal({
            launchContract,
            openCodeTimeoutMs,
            openCodeWorkdir,
            env: buildOpenCodeEnv(input),
            executionMode: input.executionMode,
            instructionPrompt: input.instructionPrompt,
            conversationPolicy: input.conversationPolicy,
            ...(input.onInteractionRequest
                ? { onInteractionRequest: input.onInteractionRequest }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(onProcessControlEvent ? { onProcessControlEvent } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            sessionId: normalizeOptionalText(input.agentControllerConversationId),
            streamingMode: input.streamingMode,
        });
        const agentControllerConversation = OPENCODE_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: OPENCODE_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: result.sessionId,
            })
            : null;
        return {
            contextDelta: {
                agentControllerConversationId: result.sessionId,
                refreshed: result.contextHealed,
                ...(agentControllerConversation ? { agentControllerConversation } : {}),
            },
            output: result.output,
            ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        };
    };
};
function buildOpenCodeEnv(input) {
    const spaceActionMcpServer = resolveDispatchSpaceActionMcpServer(input.env);
    if (!spaceActionMcpServer) {
        if (!input.openCodePermission) {
            return input.env;
        }
        return {
            ...(input.env ?? {}),
            [OPENCODE_PERMISSION_ENV]: JSON.stringify(input.openCodePermission),
        };
    }
    const toolIds = buildDispatchSpaceActionMcpToolIds("_");
    const config = parseOpenCodeConfigContent(input.env?.[OPENCODE_CONFIG_CONTENT_ENV]);
    const mcp = readRecord(config.mcp);
    if (mcp[spaceActionMcpServer.id] !== undefined) {
        throw new Error("OpenMeld cannot start its dispatch Space Action tool because the reserved OpenCode MCP server name is already configured.");
    }
    const tools = readRecord(config.tools);
    const permission = {
        ...(input.openCodePermission ?? {}),
        ...Object.fromEntries(toolIds.map((toolId) => [toolId, "allow"])),
    };
    return {
        ...(input.env ?? {}),
        [OPENCODE_CONFIG_CONTENT_ENV]: JSON.stringify({
            ...config,
            mcp: {
                ...mcp,
                [spaceActionMcpServer.id]: {
                    command: [
                        spaceActionMcpServer.commandPath,
                        ...spaceActionMcpServer.args,
                    ],
                    enabled: true,
                    environment: spaceActionMcpServer.env,
                    type: "local",
                },
            },
            tools: {
                ...tools,
                ...Object.fromEntries(toolIds.map((toolId) => [toolId, true])),
            },
        }),
        [OPENCODE_PERMISSION_ENV]: JSON.stringify(permission),
    };
}
function parseOpenCodeConfigContent(value) {
    const normalized = normalizeOptionalText(value);
    if (!normalized) {
        return {};
    }
    let parsed;
    try {
        parsed = JSON.parse(normalized);
    }
    catch (error) {
        throw new Error("OpenCode process config is not valid JSON.", {
            cause: error,
        });
    }
    if (!(parsed && typeof parsed === "object" && !Array.isArray(parsed))) {
        throw new Error("OpenCode process config must be a JSON object.");
    }
    return parsed;
}
function readRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
const resolveOpenCodeDispatchLaunchContract = async (input) => {
    const launchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (input.openCodeBin
            ? buildExecutableLaunchContract({
                commandPath: input.openCodeBin,
            })
            : null);
    if (!launchContract) {
        throw Object.assign(new Error("OpenCode launch contract is required."), {
            code: "adapter.missing_launch_contract",
            errorType: "config",
        });
    }
    // Discovery probes CLI capabilities. A Wake validates the selected files and
    // launches the actual task; a second version process must not gate execution.
    const verification = await verifyLaunchContractFiles({
        launchContract,
    });
    if (!verification.ok) {
        throw Object.assign(new Error(`OpenCode launch contract is not executable: ${verification.errorMessage}`), {
            code: "adapter.invalid_launch_contract",
            errorType: "config",
        });
    }
    return launchContract;
};
const resolveRuntimeProcessRegistrar = (input) => {
    if (!input.registerRuntimeProcess) {
        return undefined;
    }
    return (event) => {
        input.registerRuntimeProcess?.({
            ...event,
            taskId: input.taskId,
        });
    };
};
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
